/**
 * 계약의뢰 공통 워크스페이스
 * - register: 작성·저장·의뢰 (사업부서) — 신규는 [품의 불러오기]만 (빈 신규 의뢰 없음)
 * - receive: 의뢰 건 접수/반려 (계약담당)
 *
 * 프로세스: 품의 승인 → 계약의뢰 → 계약대장 → 원인행위 → 결의
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import {
  FormArea,
  CollapsibleSearchPanel,
  CollapsibleSectionPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  ActiveGridHeader,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert, showConfirm } from 'utils/toastBridge';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import ContractRequestDocViewerDialog from 'components/expense/ContractRequestDocViewerDialog';
import {
  CONTRACT_OBJECT_PRIMARY_OPTIONS,
  AWARD_METHOD_OPTIONS,
  CONTRACT_REQUEST_STATUS,
  CONTRACT_REQUEST_STATUS_OPTIONS,
  CONTRACT_METHOD,
  CONTRACT_METHOD_OPTIONS,
  CONTRACT_REQUEST_DOC_TYPE_OPTIONS,
  createContractRequestUid,
  createContractRequestDocUid,
  emptyContractRequestMaster,
  emptyContractRequestDocRow,
  loadContractRequestBundle,
  saveContractRequestBundle,
  nextContractRequestNo,
  filterContractRequestsForRegister,
  filterContractRequestsForReceive,
  enrichContractRequestRow,
  listApprovedProposalsForContractRequest,
  applyProposalToContractRequest,
  submitContractRequest,
  receiveContractRequest,
  rejectContractRequest,
  contractRequestStatusLabel,
  contractMethodLabel,
  contractRequestDocTypeLabel,
  todayYmd,
} from 'expense/contractRequestStore';
import { contractObjectLabel, awardMethodLabel } from 'contract/contractStore';
import {
  CONTRACT_REQUEST_UPLOAD_ACCEPT,
  CONTRACT_REQUEST_VIEWABLE_EXTS,
  CONTRACT_REQUEST_FILE_MAX_BYTES,
  formatFileSize,
  isViewableExt,
  saveContractRequestFile,
  deleteContractRequestFiles,
  buildDocMetaFromFile,
} from 'expense/contractRequestFileStore';

const LIST_GRID_ID = 'expense-contract-request-list';
const DOC_GRID_ID = 'expense-contract-request-doc';
const MASTER_EXPORT_ID = 'expense-contract-request-master';
const DOC_PANEL_H = '260px';
const LIST_PANEL_H = '300px';

const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  ${hoverExpandScrollbarCss}

  > * {
    flex-shrink: 0;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: ${(p) => p.$flex || '0 0 auto'};
  height: ${(p) => p.$height || 'auto'};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const DropZone = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &[data-dragging='Y']::after {
    content: '파일을 놓으면 첨부 등록';
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    font-size: ${theme.typography.fontSize.sm};
    font-weight: 600;
    color: ${theme.colors.primary};
    background: ${theme.colors.primary}1a;
    border: 2px dashed ${theme.colors.primary};
    border-radius: ${theme.borderRadius.sm};
  }
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

const MasterToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  min-height: 34px;
  box-sizing: border-box;
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const ToolbarBtn = styled.button`
  appearance: none;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.white};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.xs};
  padding: 3px 8px;
  min-height: 26px;
  border-radius: ${theme.borderRadius.sm};
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
  &:disabled {
    background: ${theme.colors.background.light};
    color: ${theme.colors.text.secondary};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SEARCH_SLOT_W = ['230px', '280px', '330px'];
const SEARCH_DATE_W = '116px';
const MASTER_LABEL_W = '72px';

function emptySearch(partial = {}) {
  return {
    searchFiscalYear: getCurrentFiscalYear(),
    searchStatus: '',
    searchObjectType: '',
    searchDateFrom: '',
    searchDateTo: '',
    searchKeyword: '',
    ...partial,
  };
}

const SEARCH_FIELD_KEYS = [
  'searchFiscalYear',
  'searchStatus',
  'searchObjectType',
  'searchDateFrom',
  'searchDateTo',
  'searchKeyword',
];

function pickSearchValues(values = {}) {
  const out = {};
  SEARCH_FIELD_KEYS.forEach((k) => {
    out[k] = values[k] ?? '';
  });
  return out;
}

/**
 * @param {{ screenMode?: 'register'|'receive' }} props
 */
function ExpenseContractRequestWorkspaceInner({ screenMode = 'register' }) {
  const isReceive = screenMode === 'receive';
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const scrollbarHoverProps = useScrollbarHoverProps();

  const searchDefaults = useMemo(
    () =>
      emptySearch({
        searchStatus: isReceive ? CONTRACT_REQUEST_STATUS.REQUESTED : '',
      }),
    [isReceive]
  );

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      ...searchDefaults,
      ...emptyContractRequestMaster({
        fiscalYear: searchDefaults.searchFiscalYear,
        requestDate: todayYmd(),
      }),
      statusNm: '',
    },
  });
  const { getValues, reset, setValue, control } = methods;

  const listRef = useRef(null);
  const docRef = useRef(null);
  const fileInputRef = useRef(null);
  const selectedUidRef = useRef('');
  const selectedDocUidRef = useRef('');
  const [selectedUid, setSelectedUid] = useState('');
  const [selectedDocUid, setSelectedDocUid] = useState('');
  const [bundleDocs, setBundleDocs] = useState([]);
  const [listCount, setListCount] = useState(0);
  const [proposalLookupOpen, setProposalLookupOpen] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerTarget, setViewerTarget] = useState({ docUid: '', fileName: '' });
  const [docDragging, setDocDragging] = useState(false);
  const dragDepthRef = useRef(0);

  const masterStatus = useWatch({ control, name: 'status' });
  const contractMethodWatch = useWatch({ control, name: 'contractMethod' });
  const searchFiscalYearWatch = useWatch({ control, name: 'searchFiscalYear' });
  const isPrivate = contractMethodWatch === CONTRACT_METHOD.PRIVATE;
  const masterReadonly =
    isReceive ||
    (masterStatus &&
      masterStatus !== CONTRACT_REQUEST_STATUS.DRAFT &&
      masterStatus !== CONTRACT_REQUEST_STATUS.REJECTED);

  useEffect(() => {
    selectedUidRef.current = selectedUid;
  }, [selectedUid]);

  useEffect(() => {
    selectedDocUidRef.current = selectedDocUid;
  }, [selectedDocUid]);

  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) => {
      if (isReceive) return '접수 화면에서는 추가할 수 없습니다.';
      if (id === DOC_GRID_ID && !selectedUidRef.current) {
        return '계약의뢰를 선택하거나 신규 추가하세요.';
      }
      if (id === DOC_GRID_ID && masterReadonly) {
        return '작성중·반려 건만 첨부서류를 추가할 수 있습니다.';
      }
      return null;
    },
    guardDelete: () => (isReceive ? '접수 화면에서는 삭제할 수 없습니다.' : null),
  });

  const dimsFromSearch = useCallback(() => {
    const v = getValues();
    return {
      tenantCode,
      fiscalYear: String(v.searchFiscalYear || '').trim(),
    };
  }, [getValues, tenantCode]);

  const listColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '의뢰번호', field: 'requestNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '품의번호', field: 'proposalNo', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '건명', field: 'title', size: 180 }),
      hiCol.roTextColumn({ header: '목적물', field: 'objectTypeNm', size: 70, align: 'center' }),
      hiCol.roNumberColumn({ header: '설계금액', field: 'estimateAmt', size: 100, align: 'right' }),
      hiCol.roTextColumn({ header: '계약방법', field: 'contractMethodNm', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '의뢰일', field: 'requestDate', size: 100, align: 'center' }),
    ],
    []
  );

  const docColumns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '문서유형',
        field: 'docType',
        size: 120,
        options: CONTRACT_REQUEST_DOC_TYPE_OPTIONS,
        editable: !masterReadonly,
      }),
      hiCol.roTextColumn({ header: '파일명', field: 'fileName', size: 200 }),
      hiCol.roTextColumn({ header: '확장자', field: 'fileExt', size: 70, align: 'center' }),
      hiCol.roTextColumn({ header: '용량', field: 'fileSizeNm', size: 90, align: 'right' }),
      hiCol.roTextColumn({ header: '열람', field: 'viewableNm', size: 70, align: 'center' }),
      hiCol.edTextColumn({
        header: '비고',
        field: 'remark',
        size: 160,
        editable: !masterReadonly,
      }),
    ],
    [masterReadonly]
  );

  const listGrid = useHiGridHook({
    gridRef: listRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
  });

  const docGrid = useHiGridHook({
    gridRef: docRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: !isReceive && !masterReadonly,
    confirmCheck: !isReceive && !masterReadonly,
    defaultRow: emptyContractRequestDocRow(),
  });

  const enrichDocRow = useCallback((row) => {
    const ext = String(row.fileExt || '').toLowerCase();
    const viewable = row.viewableYn === 'Y' || isViewableExt(ext);
    const docUid = row.docUid || '';
    return {
      ...row,
      // HiGrid 행체크: requestUid 공유 시 전체 선택되는 것을 막기 위해 고유 id 부여
      id: docUid,
      fileSizeNm: row.hasFile === 'Y' || row.fileSize ? formatFileSize(row.fileSize) : '',
      viewableNm: row.hasFile === 'Y' ? (viewable ? '가능' : '불가') : '',
      viewableYn: viewable ? 'Y' : 'N',
    };
  }, []);

  const loadMasterToForm = useCallback(
    (row, docsSource) => {
      const searchKeep = pickSearchValues(getValues());
      if (!row) {
        reset({
          ...searchKeep,
          ...emptyContractRequestMaster({
            fiscalYear: searchKeep.searchFiscalYear,
            requestDate: todayYmd(),
          }),
          statusNm: '',
        });
        docRef.current?.setData?.([]);
        setSelectedDocUid('');
        return;
      }
      reset({
        ...searchKeep,
        ...row,
        statusNm: contractRequestStatusLabel(row.status),
      });
      // docsSource 우선 — 조회 직후 setBundleDocs 반영 전 stale state로 첨부가 비는 것 방지
      const source = docsSource != null ? docsSource : bundleDocs;
      const docs = (source || [])
        .filter((d) => d.requestUid === row.requestUid && d.mod !== 'D')
        .map((d) => enrichDocRow({ ...d, mod: '' }));
      docRef.current?.setData?.(docs);
      setSelectedDocUid(docs[0]?.docUid || '');
    },
    [reset, getValues, bundleDocs, enrichDocRow]
  );

  const handleSearch = useCallback(async () => {
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const v = getValues();
    const filter = {
      status: String(v.searchStatus || '').trim() || undefined,
      objectType: String(v.searchObjectType || '').trim() || undefined,
      dateFrom: String(v.searchDateFrom || '').trim() || undefined,
      dateTo: String(v.searchDateTo || '').trim() || undefined,
      keyword: String(v.searchKeyword || '').trim() || undefined,
    };
    const bundle = await loadContractRequestBundle(dims);
    let masters = isReceive
      ? filterContractRequestsForReceive(bundle.masters, filter)
      : filterContractRequestsForRegister(bundle.masters, filter);
    masters = masters.map((m) => enrichContractRequestRow({ ...m, mod: '' }));
    const docs = (bundle.docs || []).map((d) => (d.mod === 'D' ? d : { ...d, mod: '' }));
    setBundleDocs(docs);
    listRef.current?.setData?.(masters);
    setListCount(masters.length);
    const first = masters[0];
    setSelectedUid(first?.requestUid || '');
    loadMasterToForm(first, docs);
  }, [dimsFromSearch, getValues, isReceive, loadMasterToForm]);

  useEffect(() => {
    reset({
      ...searchDefaults,
      ...emptyContractRequestMaster({
        fiscalYear: searchDefaults.searchFiscalYear,
        requestDate: todayYmd(),
      }),
      statusNm: '',
    });
  }, [reset, searchDefaults]);

  const onListSelect = useCallback(
    (row) => {
      setSelectedUid(row?.requestUid || '');
      loadMasterToForm(row);
    },
    [loadMasterToForm]
  );

  useEffect(() => {
    // onRowClicked: ({ data, rowIndex }) / onSelectRow: (rowData, rowIndex) — 시그니처가 다름
    listGrid.onRowClicked = ({ data }) => onListSelect(data);
    listGrid.onSelectRow = (row) => onListSelect(row);
  }, [listGrid, onListSelect]);

  useEffect(() => {
    docGrid.onRowClicked = ({ data }) => {
      setSelectedDocUid(data?.docUid || '');
    };
    // 포커스 이동 시 onSelectRow가 호출됨. ({ data })로 받으면 row.docUid를 놓쳐 선택이 비워짐 → 열람 비활성
    docGrid.onSelectRow = (row) => {
      setSelectedDocUid(row?.docUid || '');
    };
  }, [docGrid]);

  const collectDocsFromGrid = useCallback(() => {
    const rows = docRef.current?.getData?.() || [];
    const uid = selectedUidRef.current;
    return rows
      .filter((r) => r.mod !== 'D')
      .map((r) => ({
        ...r,
        requestUid: uid,
        docUid: r.docUid || createContractRequestDocUid(),
      }));
  }, []);

  /** 품의 불러오기 = 신규 계약의뢰 시작 (품의 없이 빈 의뢰 작성 불가) */
  const openProposalLookup = useCallback(() => {
    if (isReceive) return;
    setProposalLookupOpen(true);
  }, [isReceive]);

  const startRequestFromProposal = useCallback(
    async (proposal) => {
      if (isReceive || !proposal) return false;

      if (selectedUidRef.current) {
        const st = String(getValues('status') || '');
        const editing =
          st === CONTRACT_REQUEST_STATUS.DRAFT || st === CONTRACT_REQUEST_STATUS.REJECTED || !st;
        if (editing) {
          const ok = await showConfirm(
            '선택한 품의로 새 계약의뢰를 작성합니다.\n현재 입력 중인 내용은 저장되지 않습니다. 계속할까요?'
          );
          if (!ok) return false;
        } else {
          // 의뢰·접수 등 확정 흐름 건을 보고 있어도 품의 불러오기는 신규 작성
          const ok = await showConfirm('선택한 품의로 새 계약의뢰를 작성할까요?');
          if (!ok) return false;
        }
      }

      const dims = dimsFromSearch();
      const bundle = await loadContractRequestBundle(dims);
      const uid = createContractRequestUid();
      const base = enrichContractRequestRow(
        emptyContractRequestMaster({
          requestUid: uid,
          requestNo: nextContractRequestNo(bundle.masters),
          fiscalYear: dims.fiscalYear || getCurrentFiscalYear(),
          requestDate: todayYmd(),
          status: CONTRACT_REQUEST_STATUS.DRAFT,
          registerDate: todayYmd(),
          requesterName: user?.userNm || user?.name || '',
          mod: 'A',
        })
      );

      setSelectedUid(uid);
      selectedUidRef.current = uid;
      reset({
        ...pickSearchValues(getValues()),
        ...base,
        statusNm: base.statusNm || contractRequestStatusLabel(base.status),
      });
      docRef.current?.setData?.([]);
      setSelectedDocUid('');

      applyProposalToContractRequest((fields) => {
        Object.entries(fields).forEach(([k, v]) => setValue(k, v));
      }, proposal);

      const overview = String(getValues('overview') || '').trim();
      if (!overview) {
        setValue('overview', proposal.remark || '');
      }
      setValue('statusNm', contractRequestStatusLabel(CONTRACT_REQUEST_STATUS.DRAFT));
      return true;
    },
    [isReceive, dimsFromSearch, getValues, reset, setValue, user]
  );

  const handleSave = useCallback(async () => {
    if (isReceive) {
      showAlert('접수 화면에서는 저장 대신 접수/반려를 사용하세요.');
      return;
    }
    const dims = dimsFromSearch();
    if (!dims.fiscalYear) {
      showAlert('회계연도를 확인하세요.');
      return;
    }
    let uid = selectedUidRef.current;
    if (!uid) {
      uid = createContractRequestUid();
      selectedUidRef.current = uid;
      setSelectedUid(uid);
    }

    const form = getValues();
    if (!String(form.title || '').trim()) {
      showAlert('건명을 입력하세요.');
      return;
    }
    if (!String(form.proposalUid || '').trim()) {
      showAlert('승인된 품의를 불러오세요.');
      return;
    }
    if (!Number(form.estimateAmt)) {
      showAlert('설계(추정)금액을 입력하세요.');
      return;
    }
    if (!form.periodFrom || !form.periodTo) {
      showAlert('계약기간(시작·종료)을 입력하세요.');
      return;
    }
    if (form.contractMethod === CONTRACT_METHOD.PRIVATE) {
      if (!String(form.privateContractBasis || '').trim() || !String(form.privateContractReason || '').trim()) {
        showAlert('수의계약인 경우 근거와 사유를 입력하세요.');
        return;
      }
    }

    const bundle = await loadContractRequestBundle(dims);
    const docsForUid = collectDocsFromGrid()
      .filter((d) => d.mod !== 'D')
      .map((d) => ({
        ...d,
        // 저장 완료 행은 상태열(A/U) 표시 해제
        mod: '',
      }));
    let masters = [...(bundle.masters || [])];
    const idx = masters.findIndex((m) => m.requestUid === uid);
    const masterRow = {
      ...emptyContractRequestMaster(),
      ...form,
      requestUid: uid,
      requestNo: form.requestNo || nextContractRequestNo(masters),
      fiscalYear: dims.fiscalYear,
      registerDate: form.registerDate || todayYmd(),
      status: form.status || CONTRACT_REQUEST_STATUS.DRAFT,
      mod: '',
    };
    if (idx >= 0) masters[idx] = masterRow;
    else masters.push(masterRow);

    // 삭제(mod=D) 건은 저장 시 제외 · 파일도 IndexedDB에서 제거
    const prevDocs = (bundle.docs || []).filter((d) => d.requestUid === uid);
    const keepUids = new Set(docsForUid.map((d) => d.docUid).filter(Boolean));
    const removedUids = prevDocs
      .map((d) => d.docUid)
      .filter((id) => id && !keepUids.has(id));

    let allDocs = (bundle.docs || []).filter((d) => d.requestUid !== uid);
    allDocs = [...allDocs, ...docsForUid];

    const saved = await saveContractRequestBundle(dims, masters, allDocs);
    setBundleDocs(saved.docs);
    listRef.current?.setData?.(
      saved.masters
        .filter((m) => m.mod !== 'D')
        .map((m) => enrichContractRequestRow({ ...m, mod: '' }))
    );
    // 첨부 그리드도 저장본으로 갱신 → A/U 아이콘 제거
    const savedDocs = (saved.docs || [])
      .filter((d) => d.requestUid === uid && d.mod !== 'D')
      .map(enrichDocRow);
    docRef.current?.setData?.(savedDocs);
    if (removedUids.length) {
      await deleteContractRequestFiles(removedUids);
    }
    setValue('statusNm', contractRequestStatusLabel(masterRow.status));
    setValue('mod', '');
    showAlert('저장했습니다.', 'success');
  }, [isReceive, dimsFromSearch, getValues, collectDocsFromGrid, setValue, enrichDocRow]);

  const handleSubmitRequest = useCallback(async () => {
    if (isReceive) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('의뢰할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== CONTRACT_REQUEST_STATUS.DRAFT && form.status !== CONTRACT_REQUEST_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 의뢰할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('계약담당에게 의뢰할까요?');
    if (!ok) return;
    await handleSave();
    const dims = dimsFromSearch();
    const bundle = await loadContractRequestBundle(dims);
    const { masters, docs } = submitContractRequest(bundle.masters, bundle.docs, uid);
    await saveContractRequestBundle(dims, masters, docs);
    await handleSearch();
    showAlert('의뢰했습니다.', 'success');
  }, [isReceive, getValues, handleSave, dimsFromSearch, handleSearch]);

  const handleReceive = useCallback(async () => {
    if (!isReceive) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('접수할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== CONTRACT_REQUEST_STATUS.REQUESTED) {
      showAlert('의뢰 상태인 건만 접수할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('접수할까요? (이후 계약대장에서 공고·체결을 진행합니다)');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadContractRequestBundle(dims);
    const { masters, docs } = receiveContractRequest(bundle.masters, bundle.docs, uid);
    await saveContractRequestBundle(dims, masters, docs);
    await handleSearch();
    showAlert('접수했습니다.', 'success');
  }, [isReceive, getValues, dimsFromSearch, handleSearch]);

  const handleReject = useCallback(async () => {
    if (!isReceive) return;
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('반려할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== CONTRACT_REQUEST_STATUS.REQUESTED) {
      showAlert('의뢰 상태인 건만 반려할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('반려할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadContractRequestBundle(dims);
    const { masters, docs } = rejectContractRequest(bundle.masters, bundle.docs, uid);
    await saveContractRequestBundle(dims, masters, docs);
    await handleSearch();
    showAlert('반려했습니다.', 'success');
  }, [isReceive, getValues, dimsFromSearch, handleSearch]);

  const handleDeleteCurrent = useCallback(async () => {
    if (isReceive) return;
    // 첨부서류 활성 + 행이 있으면 그리드 행 삭제, 없으면 마스터 삭제
    if (activeGridId === DOC_GRID_ID) {
      const rows = (docRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
      if (rows.length > 0) {
        await handleActiveDelete();
        return;
      }
    }
    const uid = selectedUidRef.current;
    if (!uid) {
      showAlert('삭제할 건을 선택하세요.');
      return;
    }
    const form = getValues();
    if (form.status !== CONTRACT_REQUEST_STATUS.DRAFT && form.status !== CONTRACT_REQUEST_STATUS.REJECTED) {
      showAlert('작성중·반려 건만 삭제할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('선택한 계약의뢰를 삭제할까요?');
    if (!ok) return;
    const dims = dimsFromSearch();
    const bundle = await loadContractRequestBundle(dims);
    const docUids = (bundle.docs || [])
      .filter((d) => d.requestUid === uid)
      .map((d) => d.docUid)
      .filter(Boolean);
    const masters = (bundle.masters || []).map((m) =>
      m.requestUid === uid ? { ...m, mod: 'D' } : m
    );
    const docs = (bundle.docs || []).map((d) =>
      d.requestUid === uid ? { ...d, mod: 'D' } : d
    );
    await saveContractRequestBundle(dims, masters, docs);
    await deleteContractRequestFiles(docUids);
    await handleSearch();
    showAlert('삭제했습니다.', 'success');
  }, [isReceive, activeGridId, handleActiveDelete, getValues, dimsFromSearch, handleSearch]);

  const handleDeleteDocs = useCallback(async () => {
    const selected = docRef.current?.getSelectedRow?.() || docGrid.getSelectedRow?.();
    const checked = docRef.current?.getCheckedData?.() || [];
    const targets = (Array.isArray(checked) && checked.length ? checked : selected ? [selected] : [])
      .map((r) => r?.docUid)
      .filter(Boolean);

    await docGrid.handleDelete?.();

    if (targets.length) {
      await deleteContractRequestFiles(targets);
    }
    const remain = (docRef.current?.getData?.() || []).filter((r) => r.mod !== 'D');
    setSelectedDocUid(remain[0]?.docUid || '');
  }, [docGrid]);

  const dispatchDocAdd = useCallback(async () => {
    if (isReceive || masterReadonly) return;
    if (!selectedUidRef.current) {
      showAlert('계약의뢰를 먼저 선택하거나 신규 추가하세요.');
      return;
    }
    const uid = createContractRequestDocUid();
    const rows = docRef.current?.getData?.() || [];
    const next = [
      ...rows,
      enrichDocRow(
        emptyContractRequestDocRow({
          docUid: uid,
          requestUid: selectedUidRef.current,
          mod: 'A',
        })
      ),
    ];
    docRef.current?.setData?.(next);
    setSelectedDocUid(uid);
  }, [isReceive, masterReadonly, enrichDocRow]);

  const handleUploadClick = useCallback(() => {
    if (isReceive || masterReadonly) {
      showAlert('작성중·반려 건만 파일을 업로드할 수 있습니다.');
      return;
    }
    if (!selectedUidRef.current) {
      showAlert('계약의뢰를 먼저 선택하거나 신규 추가하세요.');
      return;
    }
    fileInputRef.current?.click?.();
  }, [isReceive, masterReadonly]);

  /** 단일·다중 파일 등록 (버튼 / DnD 공통) — 파일마다 첨부 행 생성 */
  const handleFilesUpload = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length) return;

      if (isReceive || masterReadonly) {
        showAlert('작성중·반려 건만 파일을 업로드할 수 있습니다.');
        return;
      }
      const requestUid = selectedUidRef.current;
      if (!requestUid) {
        showAlert('계약의뢰를 먼저 선택하세요.');
        return;
      }

      let rows = [...(docRef.current?.getData?.() || [])];
      let okCount = 0;
      let failCount = 0;
      const failMsgs = [];
      let lastDocUid = selectedDocUidRef.current;

      for (const file of files) {
        if (file.size > CONTRACT_REQUEST_FILE_MAX_BYTES) {
          failCount += 1;
          failMsgs.push(
            `${file.name}: 용량 초과 (최대 ${formatFileSize(CONTRACT_REQUEST_FILE_MAX_BYTES)})`
          );
          continue;
        }

        const docUid = createContractRequestDocUid();
        try {
          const meta = await saveContractRequestFile(docUid, {
            requestUid,
            file,
            fileName: file.name,
          });
          rows.push(
            enrichDocRow(
              emptyContractRequestDocRow({
                docUid,
                requestUid,
                ...meta,
                ...buildDocMetaFromFile(file),
                mod: 'A',
              })
            )
          );
          lastDocUid = docUid;
          okCount += 1;
        } catch (err) {
          failCount += 1;
          failMsgs.push(`${file.name}: ${err?.message || '실패'}`);
        }
      }

      docRef.current?.setData?.(rows);
      if (lastDocUid) setSelectedDocUid(lastDocUid);

      if (okCount && !failCount) {
        showAlert(`${okCount}건 업로드했습니다. [열람]으로 확인할 수 있습니다.`, 'success');
      } else if (okCount && failCount) {
        showAlert(
          `${okCount}건 성공 · ${failCount}건 실패\n${failMsgs.slice(0, 5).join('\n')}`,
          'warning'
        );
      } else {
        showAlert(failMsgs.slice(0, 5).join('\n') || '업로드에 실패했습니다.');
      }
    },
    [isReceive, masterReadonly, enrichDocRow]
  );

  const handleFilePicked = useCallback(
    async (e) => {
      const list = e.target.files;
      e.target.value = '';
      if (!list?.length) return;
      await handleFilesUpload(list);
    },
    [handleFilesUpload]
  );

  const onDocDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isReceive || masterReadonly || !selectedUidRef.current) return;
      if (![...e.dataTransfer.types].includes('Files')) return;
      dragDepthRef.current += 1;
      setDocDragging(true);
    },
    [isReceive, masterReadonly]
  );

  const onDocDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDocDragging(false);
  }, []);

  const onDocDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  }, []);

  const onDocDrop = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepthRef.current = 0;
      setDocDragging(false);
      const files = e.dataTransfer?.files;
      if (!files?.length) return;
      await handleFilesUpload(files);
    },
    [handleFilesUpload]
  );

  const handleViewClick = useCallback(() => {
    const row =
      docRef.current?.getSelectedRow?.() ||
      (docRef.current?.getData?.() || []).find((r) => r.docUid === selectedDocUidRef.current);

    if (!row?.docUid) {
      showAlert('열람할 첨부 행을 선택하세요.');
      return;
    }
    if (row.hasFile !== 'Y' && !row.fileName) {
      showAlert('업로드된 파일이 없습니다. [업로드]로 파일을 등록하세요.');
      return;
    }
    if (row.viewableYn !== 'Y' && !isViewableExt(row.fileExt || row.fileName)) {
      showAlert(
        `이 파일은 인앱 열람을 지원하지 않습니다.\n지원 확장자: ${CONTRACT_REQUEST_VIEWABLE_EXTS.join(', ')}`
      );
      return;
    }
    setViewerTarget({ docUid: row.docUid, fileName: row.fileName || '' });
    setViewerOpen(true);
  }, []);

  /**
   * F8/추가 — 마스터 없으면 품의 불러오기(신규 의뢰), 있으면 첨부 행 추가
   */
  const handleAdd = useCallback(() => {
    if (isReceive) return;
    if (!selectedUidRef.current) {
      openProposalLookup();
      return;
    }
    if (masterReadonly) {
      showAlert('작성중·반려 건만 첨부서류를 추가할 수 있습니다.');
      return;
    }
    handleActiveAdd();
  }, [isReceive, masterReadonly, handleActiveAdd, openProposalLookup]);

  const [proposalLookupRows, setProposalLookupRows] = useState([]);

  useEffect(() => {
    if (!proposalLookupOpen) return;
    let live = true;
    (async () => {
      const dims = dimsFromSearch();
      const rows = await listApprovedProposalsForContractRequest(dims);
      if (live) setProposalLookupRows(rows);
    })();
    return () => {
      live = false;
    };
  }, [proposalLookupOpen, dimsFromSearch, searchFiscalYearWatch]);

  const proposalLookupConfig = useMemo(() => {
    return {
      title: '승인 품의 조회 — 계약의뢰 신규',
      rows: proposalLookupRows,
      columns: [
        hiCol.roTextColumn({ header: '품의번호', field: 'proposalNo', size: 80, align: 'center' }),
        hiCol.roTextColumn({ header: '부서', field: 'deptName', size: 100 }),
        hiCol.roTextColumn({ header: '과목', field: 'subjectName', size: 120 }),
        hiCol.roNumberColumn({ header: '품의금액', field: 'proposalAmt', size: 100, align: 'right' }),
        hiCol.roTextColumn({ header: '품의일', field: 'proposalDate', size: 100, align: 'center' }),
        hiCol.roTextColumn({ header: '적요', field: 'remark', size: 160 }),
      ],
      enableSearch: true,
      searchConfig: {
        mode: 'unified',
        keywordFields: ['proposalNo', 'deptName', 'subjectName', 'remark'],
        hint: '승인 품의를 선택하면 계약의뢰 초안이 생성됩니다. (품의 필수)',
      },
    };
  }, [proposalLookupRows]);

  const applyProposal = useCallback(
    (row) => {
      const r = Array.isArray(row) ? row[0] : row;
      if (!r) return false;
      return startRequestFromProposal(r);
    },
    [startRequestFromProposal]
  );

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'searchFiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          parentWidth: SEARCH_SLOT_W[0],
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchStatus',
          label: '상태',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...CONTRACT_REQUEST_STATUS_OPTIONS],
          parentWidth: SEARCH_SLOT_W[1],
          width: '140px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchRequestDateRange',
          label: '의뢰일',
          type: 'dateRange',
          startFieldName: 'searchDateFrom',
          endFieldName: 'searchDateTo',
          parentWidth: SEARCH_SLOT_W[2],
          width: SEARCH_DATE_W,
          labelWidth: SEARCH_LABEL_W,
          fiscalYear: searchFiscalYearWatch || getCurrentFiscalYear(),
          presetSet: 'inquiry',
        },
      ],
      [
        {
          name: 'searchObjectType',
          label: '목적물',
          type: 'select',
          options: [{ value: '', label: '전체' }, ...CONTRACT_OBJECT_PRIMARY_OPTIONS],
          parentWidth: SEARCH_SLOT_W[0],
          width: '120px',
          labelWidth: SEARCH_LABEL_W,
        },
        {
          name: 'searchKeyword',
          label: '검색',
          type: 'text',
          placeholder: '의뢰번호·건명·품의번호',
          parentWidth: SEARCH_SLOT_W[1],
          width: '240px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    [searchFiscalYearWatch]
  );

  const masterFields = useMemo(() => {
    const ro = masterReadonly;
    const L = MASTER_LABEL_W;
    return [
      [
        { name: 'requestNo', label: '의뢰번호', type: 'text', readOnly: true, width: '88px', labelWidth: L },
        {
          name: 'proposalNo',
          label: '품의번호',
          type: 'text',
          readOnly: true,
          width: '88px',
          labelWidth: L,
          // 등록 화면에서는 항상 품의 불러오기(신규 의뢰) 가능
          showSearch: !isReceive,
          searchTitle: '승인 품의 — 신규 의뢰',
          onSearch: openProposalLookup,
        },
        {
          name: 'requestDate',
          label: '의뢰일',
          type: 'date',
          width: '130px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'statusNm',
          label: '상태',
          type: 'text',
          readOnly: true,
          width: '80px',
          labelWidth: L,
        },
        {
          name: 'objectType',
          label: '목적물',
          type: 'select',
          options: CONTRACT_OBJECT_PRIMARY_OPTIONS,
          width: '110px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'estimateAmt',
          label: '설계금액',
          type: 'number',
          width: '120px',
          labelWidth: L,
          readOnly: ro,
        },
      ],
      [
        {
          name: 'title',
          label: '건명',
          type: 'text',
          width: '360px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '계약 건명',
        },
        {
          name: 'overview',
          label: '개요',
          type: 'text',
          width: '360px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '사업·과업 요약',
        },
      ],
      [
        { name: 'deptName', label: '부서', type: 'text', readOnly: true, width: '120px', labelWidth: L },
        { name: 'projectName', label: '사업', type: 'text', readOnly: true, width: '160px', labelWidth: L },
        { name: 'subjectName', label: '과목', type: 'text', readOnly: true, width: '160px', labelWidth: L },
        { name: 'fundName', label: '재원', type: 'text', readOnly: true, width: '100px', labelWidth: L },
        { name: 'allocAmt', label: '현액', type: 'number', readOnly: true, width: '110px', labelWidth: L },
      ],
      [
        {
          name: 'periodFrom',
          label: '기간시작',
          type: 'date',
          width: '130px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'periodTo',
          label: '기간종료',
          type: 'date',
          width: '130px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'deliveryPlace',
          label: '이행장소',
          type: 'text',
          width: '200px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '납품·이행 장소',
        },
        {
          name: 'jointContractYn',
          label: '공동계약',
          type: 'select',
          options: YN_OPTIONS,
          width: '80px',
          labelWidth: L,
          readOnly: ro,
        },
      ],
      [
        {
          name: 'contractMethod',
          label: '계약방법',
          type: 'select',
          options: CONTRACT_METHOD_OPTIONS,
          width: '120px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'awardMethod',
          label: '낙찰방법',
          type: 'select',
          options: AWARD_METHOD_OPTIONS,
          width: '130px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'preSpecOpenYn',
          label: '사전규격',
          type: 'select',
          options: YN_OPTIONS,
          width: '80px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'socialValueYn',
          label: '사회적가치',
          type: 'select',
          options: YN_OPTIONS,
          width: '80px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'hopeEnterpriseYn',
          label: '희망기업',
          type: 'select',
          options: YN_OPTIONS,
          width: '80px',
          labelWidth: L,
          readOnly: ro,
        },
      ],
      [
        {
          name: 'bidQualReq',
          label: '참가자격',
          type: 'text',
          width: '520px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '입찰참가자격요건',
        },
        {
          name: 'defectWarrantyPeriod',
          label: '하자기간',
          type: 'text',
          width: '100px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '예: 1년',
        },
        {
          name: 'defectBondRate',
          label: '하자보증률',
          type: 'text',
          width: '90px',
          labelWidth: L,
          readOnly: ro,
          placeholder: '%',
        },
      ],
      ...(isPrivate
        ? [
            [
              {
                name: 'privateContractBasis',
                label: '수의근거',
                type: 'text',
                width: '240px',
                labelWidth: L,
                readOnly: ro,
                placeholder: '법령·조항',
              },
              {
                name: 'privateContractReason',
                label: '수의사유',
                type: 'text',
                width: '280px',
                labelWidth: L,
                readOnly: ro,
              },
              {
                name: 'suggestVendorName',
                label: '상대방제안',
                type: 'text',
                width: '160px',
                labelWidth: L,
                readOnly: ro,
              },
            ],
          ]
        : []),
      [
        {
          name: 'requestDeptName',
          label: '의뢰부서',
          type: 'text',
          width: '140px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'requesterName',
          label: '의뢰자',
          type: 'text',
          width: '120px',
          labelWidth: L,
          readOnly: ro,
        },
        {
          name: 'remark',
          label: '적요',
          type: 'text',
          width: '360px',
          labelWidth: L,
          readOnly: ro,
        },
      ],
    ];
  }, [masterReadonly, isPrivate, isReceive, openProposalLookup]);

  const getMasterExportData = useCallback(() => {
    const v = getValues();
    const uid = String(v.requestUid || selectedUidRef.current || '').trim();
    if (!uid) return [];
    return [
      {
        ...v,
        objectTypeNm: contractObjectLabel(v.objectType),
        contractMethodNm: contractMethodLabel(v.contractMethod),
        awardMethodNm: awardMethodLabel(v.awardMethod),
        statusNm: contractRequestStatusLabel(v.status) || v.statusNm || '',
      },
    ];
  }, [getValues]);

  const getMasterExportCols = useCallback(
    () => [
      { header: '의뢰번호', accessorKey: 'requestNo' },
      { header: '품의번호', accessorKey: 'proposalNo' },
      { header: '건명', accessorKey: 'title' },
      { header: '개요', accessorKey: 'overview' },
      { header: '목적물', accessorKey: 'objectTypeNm' },
      { header: '설계금액', accessorKey: 'estimateAmt' },
      { header: '부서', accessorKey: 'deptName' },
      { header: '사업', accessorKey: 'projectName' },
      { header: '과목', accessorKey: 'subjectName' },
      { header: '기간시작', accessorKey: 'periodFrom' },
      { header: '기간종료', accessorKey: 'periodTo' },
      { header: '계약방법', accessorKey: 'contractMethodNm' },
      { header: '낙찰방법', accessorKey: 'awardMethodNm' },
      { header: '상태', accessorKey: 'statusNm' },
      { header: '적요', accessorKey: 'remark' },
    ],
    []
  );

  const getDocExportData = useCallback(
    () =>
      (docRef.current?.getFilteredData?.() ?? docRef.current?.getData?.() ?? [])
        .filter((r) => r.mod !== 'D')
        .map((r) => ({
          ...r,
          docTypeNm: contractRequestDocTypeLabel(r.docType),
        })),
    []
  );
  const getDocExportCols = useCallback(
    () => [
      { header: '문서유형', accessorKey: 'docTypeNm' },
      { header: '파일명', accessorKey: 'fileName' },
      { header: '확장자', accessorKey: 'fileExt' },
      { header: '용량', accessorKey: 'fileSizeNm' },
      { header: '열람', accessorKey: 'viewableNm' },
      { header: '비고', accessorKey: 'remark' },
    ],
    []
  );

  useRegisterGridExport({
    id: MASTER_EXPORT_ID,
    label: '계약의뢰마스터',
    getData: getMasterExportData,
    getDataColumns: getMasterExportCols,
    joinKeys: ['requestUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: DOC_GRID_ID,
    label: '첨부서류',
    getData: getDocExportData,
    getDataColumns: getDocExportCols,
    joinKeys: ['requestUid'],
    order: 1,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: isReceive ? '계약의뢰접수' : '계약의뢰등록',
  });

  const actionButtons = useMemo(() => {
    const base = [
      {
        id: 'search',
        label: '조회',
        icon: '🔍',
        onClick: handleSearch,
        hotkey: 'F7',
        title: '조회 (F7)',
      },
    ];
    if (isReceive) return base;
    const addToDocs = !!selectedUid && !masterReadonly;
    return [
      ...base,
      {
        id: 'new',
        label: '추가',
        icon: '➕',
        onClick: handleAdd,
        hotkey: 'F8',
        title: addToDocs
          ? `추가 → ${activeLabel || '첨부서류'} (F8)`
          : '품의 불러오기 — 신규 계약의뢰 (F8)',
      },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: handleDeleteCurrent,
        hotkey: 'F9',
        variant: 'danger',
        title:
          activeGridId === DOC_GRID_ID && activeLabel
            ? `삭제 → ${activeLabel} (F9) · 첨부 없으면 마스터 삭제`
            : '삭제 (F9)',
      },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
        title: '저장 (F10)',
      },
      {
        id: 'excel',
        label: '엑셀',
        icon: '📥',
        onClick: openExcelDialog,
        hotkey: 'F11',
        title: '엑셀 다운로드 (F11)',
      },
    ];
  }, [
    isReceive,
    handleSearch,
    handleAdd,
    handleDeleteCurrent,
    handleSave,
    openExcelDialog,
    activeLabel,
    activeGridId,
    selectedUid,
    masterReadonly,
  ]);

  const extraButtons = useMemo(() => {
    if (isReceive) {
      return [
        { id: 'receive', label: '접수', onClick: handleReceive, variant: 'primary' },
        { id: 'reject', label: '반려', onClick: handleReject, variant: 'danger' },
      ];
    }
    return [{ id: 'submit', label: '의뢰', onClick: handleSubmitRequest, variant: 'primary' }];
  }, [isReceive, handleReceive, handleReject, handleSubmitRequest]);

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack {...scrollbarHoverProps}>
          <CollapsibleSearchPanel
            storageKey={`expense-contract-request-${screenMode}`}
            prefsUserId={prefsUserId}
            title="조회조건"
          >
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>
            {isReceive
              ? '의뢰 건 선택 후 접수/반려 · 첨부는 [열람]으로 확인 (다운로드 없음)'
              : `신규 의뢰는 [품의 불러오기]로만 시작 · 품의정보 + 계약항목·첨부 후 의뢰 · 첨부 DnD 지원: ${CONTRACT_REQUEST_VIEWABLE_EXTS.join(', ')}`}
            {activeLabel ? ` · 활성: ${activeLabel}` : ''}
          </Hint>

          <Panel>
            <GridExportFocusScope gridId={MASTER_EXPORT_ID} style={{ flex: '0 0 auto' }}>
              <MasterToolbar>
                <strong style={{ fontSize: theme.typography.fontSize.sm }}>계약의뢰 마스터</strong>
                <ToolbarBtn
                  type="button"
                  onClick={openProposalLookup}
                  disabled={isReceive}
                  title="승인 품의를 불러와 새 계약의뢰를 작성합니다"
                >
                  품의 불러오기 ···
                </ToolbarBtn>
              </MasterToolbar>
              <FormArea dense fields={masterFields} />
            </GridExportFocusScope>
          </Panel>

          <Panel $flex="1 0 auto" $height={DOC_PANEL_H}>
            <GridExportFocusScope gridId={DOC_GRID_ID}>
              <ActiveGridScope
                gridId={DOC_GRID_ID}
                label="첨부서류"
                order={20}
                onAdd={dispatchDocAdd}
                onDelete={handleDeleteDocs}
                canAdd={!isReceive && !masterReadonly}
                canDelete={!isReceive && !masterReadonly}
              >
                <ActiveGridHeader
                  gridId={DOC_GRID_ID}
                  extra={
                    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <ToolbarBtn
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUploadClick();
                        }}
                        disabled={isReceive || masterReadonly || !selectedUid}
                        title={
                          !selectedUid
                            ? '계약의뢰를 먼저 선택하세요.'
                            : `파일 업로드 (${CONTRACT_REQUEST_UPLOAD_ACCEPT})`
                        }
                      >
                        업로드
                      </ToolbarBtn>
                      <ToolbarBtn
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewClick();
                        }}
                        disabled={!selectedDocUid}
                        title={`인앱 열람 (지원: ${CONTRACT_REQUEST_VIEWABLE_EXTS.join(', ')})`}
                      >
                        열람
                      </ToolbarBtn>
                    </span>
                  }
                >
                  첨부서류 (업로드 · DnD · 인앱 열람)
                </ActiveGridHeader>
                <DropZone
                  data-dragging={docDragging ? 'Y' : 'N'}
                  onDragEnter={onDocDragEnter}
                  onDragLeave={onDocDragLeave}
                  onDragOver={onDocDragOver}
                  onDrop={onDocDrop}
                >
                  <GridBody>
                    <HiGridComponent
                      ref={docRef}
                      columns={docColumns}
                      showRowNumber
                      rowNumberSize={48}
                      enableColumnFilters
                      enableContextMenu={!isReceive && !masterReadonly}
                      enableRowEditMenu={!isReceive && !masterReadonly}
                      enableCut={!isReceive && !masterReadonly}
                      enableFindReplace
                      allowReplace={!isReceive && !masterReadonly}
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="계약의뢰첨부"
                      gridId={`expense-contract-request-doc-${screenMode}`}
                      prefsUserId={prefsUserId}
                      headerActive={activeGridId === DOC_GRID_ID}
                      {...(isReceive || masterReadonly ? {} : docGrid.contextMenuProps || {})}
                    />
                  </GridBody>
                </DropZone>
              </ActiveGridScope>
            </GridExportFocusScope>
          </Panel>

          <CollapsibleSectionPanel
            storageKey={`expense-contract-request-list-${screenMode}`}
            prefsUserId={prefsUserId}
            title="계약의뢰 목록"
            summary={listCount ? `${listCount}건` : '조회 후 표시'}
            defaultCollapsed
            expandedHeight={LIST_PANEL_H}
            expandedMaxHeight="none"
          >
            <GridBody>
              <HiGridComponent
                ref={listRef}
                columns={listColumns}
                showRowNumber
                rowNumberSize={48}
                showModColumn={false}
                enableRowCheck={false}
                enableColumnFilters
                enableContextMenu={false}
                enableHeaderMenu
                enableSelectionStats
                enableFreeze
                enableQuickExport
                exportFileName="계약의뢰목록"
                gridId={`expense-contract-request-list-${screenMode}`}
                prefsUserId={prefsUserId}
                headerActive={activeGridId === LIST_GRID_ID}
              />
            </GridBody>
          </CollapsibleSectionPanel>
        </Stack>
      </FormProvider>

      <CodeLookupDialog
        open={proposalLookupOpen}
        title={proposalLookupConfig.title}
        columns={proposalLookupConfig.columns}
        rows={proposalLookupConfig.rows}
        enableSearch={proposalLookupConfig.enableSearch}
        searchConfig={proposalLookupConfig.searchConfig}
        prefsUserId={prefsUserId}
        sizeKey="codeLookup-expense-contract-request-proposal"
        onSelect={applyProposal}
        onClose={() => setProposalLookupOpen(false)}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept={CONTRACT_REQUEST_UPLOAD_ACCEPT}
        multiple
        style={{ display: 'none' }}
        onChange={handleFilePicked}
      />

      <ContractRequestDocViewerDialog
        open={viewerOpen}
        onClose={() => setViewerOpen(false)}
        docUid={viewerTarget.docUid}
        fileName={viewerTarget.fileName}
        prefsUserId={prefsUserId}
      />

      {excelDialog}
    </PageContainer>
  );
}

export default function ExpenseContractRequestWorkspace({ screenMode = 'register' }) {
  return (
    <ActiveGridProvider>
      <GridExportProvider>
        <ExpenseContractRequestWorkspaceInner screenMode={screenMode} />
      </GridExportProvider>
    </ActiveGridProvider>
  );
}
